<template>
  <b-modal
    ref="modal"
    hide-footer
    size="lg"
    title="Create New API Key"
    @hidden="resetForm"
  >
    <b-overlay :show="loading">
      <b-form @submit.prevent="onSubmit">
        <b-form-row>
          <b-form-group class="col-md-6" label="Name *">
            <b-form-input
              v-model="form.name"
              type="text"
              size="sm"
              placeholder="Production API"
              required
            />
            <small class="text-muted">A descriptive name for this API key</small>
          </b-form-group>

          <b-form-group class="col-md-6" label="Environment *">
            <b-form-select
              v-model="form.environment"
              :options="environmentOptions"
              size="sm"
              required
            />
            <small class="text-muted">Sandbox for testing, Live for production</small>
          </b-form-group>
        </b-form-row>

        <b-form-row>
          <b-form-group class="col-md-6" label="Rate Limit (requests/min)">
            <b-form-input
              v-model="form.rate_limit"
              type="number"
              size="sm"
              min="1"
              placeholder="60"
            />
            <small class="text-muted">Leave empty for unlimited</small>
          </b-form-group>

          <b-form-group class="col-md-6" label="Expiration Date">
            <b-form-input
              v-model="form.expires_at"
              type="datetime-local"
              size="sm"
              :min="minDate"
            />
            <small class="text-muted">Leave empty for no expiration</small>
          </b-form-group>
        </b-form-row>

        <b-form-row>
          <b-form-group class="col-12" label="IP Whitelist">
            <b-form-textarea
              v-model="ipWhitelistText"
              placeholder="192.168.1.1&#10;10.0.0.1"
              rows="3"
              size="sm"
            />
            <small class="text-muted">One IP per line. Leave empty to allow all IPs</small>
            <div v-if="ipValidationErrors.length > 0" class="text-danger mt-2">
              <small>
                <md-icon icon="error" class="text-danger" />
                Invalid IPs: {{ ipValidationErrors.join(', ') }}
              </small>
            </div>
          </b-form-group>
        </b-form-row>

        <b-alert show variant="info" class="mt-3">
          <md-icon icon="info" />
          <strong>Note:</strong> The API key will only be shown once after creation. Make sure to save it securely.
        </b-alert>

        <div class="text-right mt-3">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="hide"
            :disabled="loading"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="loading || ipValidationErrors.length > 0"
          >
            <md-icon icon="vpn_key" />
            Generate API Key
          </b-button>
        </div>
      </b-form>
    </b-overlay>
  </b-modal>
</template>

<script>
export default {
  name: 'CreateApiKeyModal',
  props: {
    supplierId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      form: {
        name: '',
        environment: 'sandbox',
        rate_limit: null,
        expires_at: null
      },
      ipWhitelistText: '',
      environmentOptions: [
        { value: 'sandbox', text: 'Sandbox (Testing)' },
        { value: 'live', text: 'Live (Production)' }
      ]
    };
  },
  computed: {
    minDate() {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      return now.toISOString().slice(0, 16);
    },
    ipWhitelistArray() {
      return this.ipWhitelistText
        .split('\n')
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);
    },
    ipValidationErrors() {
      const errors = [];
      this.ipWhitelistArray.forEach(ip => {
        if (!this.isValidIP(ip)) {
          errors.push(ip);
        }
      });
      return errors;
    }
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    resetForm() {
      this.form = {
        name: '',
        environment: 'sandbox',
        rate_limit: null,
        expires_at: null
      };
      this.ipWhitelistText = '';
      this.loading = false;
    },
    isValidIP(ip) {
      const regex = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (!regex.test(ip)) return false;
      return ip.split('.').every(num => parseInt(num) >= 0 && parseInt(num) <= 255);
    },
    async onSubmit() {
      this.loading = true;

      const payload = {
        name: this.form.name,
        supplierId: this.supplierId,
        environment: this.form.environment,
        rate_limit: this.form.rate_limit || null,
        ip_whitelist: this.ipWhitelistArray.length > 0 ? this.ipWhitelistArray : null,
        expires_at: this.form.expires_at || null
      };

      try {
        const response = await this.$root.api.post('/suppliers/api-keys', payload);

        this.$root.showAlert('API key created successfully', { variant: 'success' });
        this.$emit('created', response.data);
        this.hide();
      } catch (error) {
        if (error.response?.status === 422) {
          this.$root.showAlert(
            error.response.data.message || 'Validation error',
            { variant: 'danger' }
          );
        } else {
          this.$root.handleRequestError(error);
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
</style>
